import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { AuthContext } from "../../utils/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, DollarSign, Bell, LogOut, Check, ChevronRight } from "lucide-react";

const G       = "var(--k-green)";
const G_DARK  = "var(--k-green-hover)";
const G_LIGHT = "var(--k-green-soft)";
const STORAGE_KEY = "komorotel_settings";

const DEFAULTS = {
  langue:   "fr",
  devise:   "eur",
  notifConfirmation: true,
  notifRappel:       true,
  notifPromo:        false,
};

const LANGUES = [
  { value: "fr", label: "Français",  flag: "🇫🇷" },
  { value: "en", label: "English",   flag: "🇬🇧" },
  { value: "ar", label: "العربية",   flag: "🇰🇲" },
];

const DEVISES = [
  { value: "eur", label: "Euro",          symbol: "€",   sub: "EUR" },
  { value: "kmf", label: "Franc comorien", symbol: "KMF", sub: "KMF" },
];

/* ── Petit toggle switch ── */
function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 99,
        background: value ? G : "#e5e7eb",
        border: "none", cursor: "pointer", position: "relative",
        transition: "background 0.22s",
        flexShrink: 0,
        WebkitTapHighlightColor: "transparent",
      }}>
      <div style={{
        position: "absolute", top: 3, left: value ? 23 : 3,
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
        transition: "left 0.22s cubic-bezier(0.34,1.56,0.64,1)",
      }} />
    </button>
  );
}

/* ── Section card ── */
function Section({ icon, title, subtitle, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      style={{ background: "#fff", borderRadius: 20, border: "1px solid #efefef", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
      <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {icon}
        </div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: 0 }}>{title}</p>
          {subtitle && <p style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0 0" }}>{subtitle}</p>}
        </div>
      </div>
      <div style={{ padding: "8px 0" }}>{children}</div>
    </motion.div>
  );
}

/* ── Ligne de choix radio ── */
function ChoiceRow({ label, sub, flag, selected, onClick, last }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      width: "100%", padding: "12px 24px",
      background: selected ? G_LIGHT : "none",
      border: "none", borderBottom: last ? "none" : "1px solid #f9fafb",
      cursor: "pointer", textAlign: "left",
      transition: "background 0.15s",
      WebkitTapHighlightColor: "transparent",
    }}
    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "#f9fafb"; }}
    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "none"; }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {flag && <span style={{ fontSize: 20 }}>{flag}</span>}
        <div>
          <p style={{ fontSize: 14, fontWeight: selected ? 700 : 500, color: selected ? G : "#374151", margin: 0 }}>{label}</p>
          {sub && <p style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0 0" }}>{sub}</p>}
        </div>
      </div>
      {selected && (
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: G, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Check size={13} color="#fff" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}

/* ── Ligne toggle ── */
function ToggleRow({ label, sub, value, onChange, last }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 24px",
      borderBottom: last ? "none" : "1px solid #f9fafb",
    }}>
      <div>
        <p style={{ fontSize: 14, fontWeight: 500, color: "#374151", margin: 0 }}>{label}</p>
        {sub && <p style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0 0" }}>{sub}</p>}
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

export default function Settings() {
  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULTS, ...JSON.parse(stored) } : DEFAULTS;
    } catch { return DEFAULTS; }
  });

  const [saved, setSaved] = useState(false);

  // Persist dès qu'un réglage change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch {}
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1800);
    return () => clearTimeout(t);
  }, [settings]);

  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    navigate("/");
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=Playfair+Display:wght@600;700&display=swap');
      `}</style>

      <Header />

      <div style={{ minHeight: "100vh", background: "#fafaf9", fontFamily: "'DM Sans', system-ui, sans-serif", paddingTop: 96, paddingBottom: 80 }}>
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "0 24px" }}>

          {/* ── En-tête ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                  <div style={{ width: 4, height: 28, borderRadius: 99, background: G }} />
                  <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(22px,5vw,28px)", fontWeight: 700, color: "#111827", margin: 0 }}>
                    Paramètres
                  </h1>
                </div>
                <p style={{ fontSize: 13, color: "#9ca3af", marginLeft: 16, fontStyle: "italic" }}>
                  Personnalisez votre expérience
                </p>
              </div>

              {/* Indicateur sauvegarde auto */}
              <AnimatePresence>
                {saved && (
                  <motion.div key="saved"
                    initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: G_LIGHT, borderRadius: 99, padding: "6px 12px" }}>
                    <Check size={12} color={G} strokeWidth={3} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: G }}>Sauvegardé</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* ── Langue ── */}
            <Section icon={<Globe size={18} color={G} />} title="Langue" subtitle="Langue d'affichage du site">
              {LANGUES.map((l, i) => (
                <ChoiceRow
                  key={l.value}
                  label={l.label}
                  flag={l.flag}
                  selected={settings.langue === l.value}
                  onClick={() => set("langue", l.value)}
                  last={i === LANGUES.length - 1}
                />
              ))}
            </Section>

            {/* ── Devise ── */}
            <Section icon={<DollarSign size={18} color={G} />} title="Devise" subtitle="Devise utilisée pour l'affichage des prix">
              {DEVISES.map((d, i) => (
                <ChoiceRow
                  key={d.value}
                  label={d.label}
                  sub={`${d.symbol} · ${d.sub}`}
                  selected={settings.devise === d.value}
                  onClick={() => set("devise", d.value)}
                  last={i === DEVISES.length - 1}
                />
              ))}
            </Section>

            {/* ── Notifications ── */}
            <Section icon={<Bell size={18} color={G} />} title="Notifications e-mail" subtitle="Choisissez les e-mails que vous souhaitez recevoir">
              <ToggleRow
                label="Confirmations de réservation"
                sub="Reçu à chaque nouvelle réservation confirmée"
                value={settings.notifConfirmation}
                onChange={v => set("notifConfirmation", v)}
              />
              <ToggleRow
                label="Rappels de séjour"
                sub="48h avant votre arrivée"
                value={settings.notifRappel}
                onChange={v => set("notifRappel", v)}
              />
              <ToggleRow
                label="Offres et promotions"
                sub="Bons plans et nouveaux hébergements"
                value={settings.notifPromo}
                onChange={v => set("notifPromo", v)}
                last
              />
            </Section>

            {/* ── Déconnexion ── */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}>
              <button
                onClick={handleLogout}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  background: "#fff", border: "1px solid #efefef", borderRadius: 16,
                  padding: "16px 24px", fontSize: 14, fontWeight: 700, color: "#dc2626",
                  cursor: "pointer", fontFamily: "inherit",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                  transition: "background 0.18s, border-color 0.18s",
                  WebkitTapHighlightColor: "transparent",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.borderColor = "#fecaca"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#efefef"; }}>
                <LogOut size={16} />
                Se déconnecter
              </button>
            </motion.div>

            {/* ── Note bas de page ── */}
            <p style={{ fontSize: 11, color: "#d1d5db", textAlign: "center", marginTop: 4 }}>
              Les préférences sont sauvegardées localement sur cet appareil.
            </p>

          </div>
        </div>
      </div>
    </>
  );
}